import React from 'react'
import styled, { css } from 'styled-components'
import { Media } from '@raysync/common/theme'
import { Navigation } from '@raysync/common/services'

const activeStyle = css`
  ${({ theme: { color }, actived, hasCursor }) => css`
    cursor: ${hasCursor ? 'pointer' : 'unset'};
    &:hover {
      color: ${color.secondary};
      ::after {
        border-top-color: ${color.secondary};
      }
    }
    color: ${actived && color.secondary} !important;
  `}
`

const MenuWrap = styled.div`
  min-width: 280px;
  background-color: ${p => p.theme.color.panel};
  border-radius: 8px;
  padding: 10px 5px 24px 0px;
  box-sizing: border-box;
  ${Media.phone`
      overflow-y: scroll;
      height: calc(100vh - .6rem);
  `}
`
const MenuItemContainer = styled.div`
  max-height: ${p => (p.isExpand ? p.maxHeight + 'px' : '32px')};
  transition: max-height 0.5s ease-in;
  overflow-y: ${p => (p.hasArrow ? 'hidden' : 'visible')};
`
const PrimaryMenuItem = styled.span`
  display: block;
  position: relative;
  color: #333;
  font-size: 14px;
  line-height: 32px;
  font-weight: 600;
  padding-left: 10px;
  ${activeStyle};
  ${p =>
    p.hasArrow &&
    css`
      ::after {
        content: '';
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-2px);
        width: 0px;
        height: 0px;
        display: inline-block;
        border-width: 6px;
        border-style: solid;
        border-color: #aaa transparent transparent;
        border-image: initial;
      }
    `}
`
const SecondaryMenuItem = styled(PrimaryMenuItem)`
  color: #636772;
  font-size: 14px;
  font-weight: normal;
  line-height: 28px;
  padding-left: 22px;
  &::before {
    position: absolute;
    content: ' ';
    display: ${p => (p.actived ? 'block' : 'none')};
    height: 26px;
    width: 2px;
    transform: translateX(-24px);
    transition: all 0.3s linear;
    background-color: ${p => p.theme.color.secondary};
  }
`
const TertiaryMenuItem = styled(PrimaryMenuItem)`
  color: #636772;
  font-weight: normal;
  font-size: 12px;
  line-height: 26px;
  padding-left: 32px;
  position: ${p => (p.hasArrow ? 'relative' : 'unset')};
  &::before {
    position: absolute;
    content: '';
    display: ${p => (p.actived ? 'block' : 'none')};
    height: 26px;
    width: 2px;
    transform: translateX(-34px);
    transition: all 0.3s linear;
    background-color: ${p => p.theme.color.secondary};
  }
`
const FourthMenuItem = styled.span`
  display: block;
  color: #636772;
  font-weight: normal;
  font-size: 12px;
  line-height: 26px;
  padding-left: 40px;
  ${activeStyle};
  &::before {
    position: absolute;
    content: ' ';
    display: ${p => (p.actived ? 'block' : 'none')};
    height: 26px;
    width: 2px;
    transform: translateX(-42px);
    transition: all 0.3s linear;
    background-color: ${p => p.theme.color.secondary};
  }
`

const SubMenuWrap = styled.div`
  border-left: 2px solid #c5d0de;
  position: relative;
  &::before {
    position: absolute;
    content: '';
    display: none;
    height: 28px;
    width: 2px;
    transform: translateX(-2px);
    transition: all 0.3s linear;
    background-color: ${p => p.theme.color.secondary};
    ${p =>
      typeof p.activeIndex !== 'undefined' &&
      css`
        display: block;
        transform: translate(-2px, ${p.activeIndex * 28}px);
      `}
  }
`
let globalMenus
const getInitialValue = (type, slug) => {
  try {
    if (!globalMenus) {
      const menus = window.sessionStorage.getItem(`${type}_menus`)
      if (menus) {
        globalMenus = menus ? JSON.parse(menus) : {}
      }
    }
    return slug in globalMenus ? globalMenus[slug] : true
  } catch (error) {}
  return true
}

const ItemContainer = ({ children, menuChildren = [], isPrimary, title, type, slug }) => {
  const [expand, setExpand] = React.useState(() => getInitialValue(type, slug))
  const hasArrow = Boolean(menuChildren)
  const newTitle = React.cloneElement(title, {
    onClick: () => {
      setExpand(value => {
        if (typeof window !== 'undefined') {
          globalMenus = { ...globalMenus, [slug]: !value }
          window.sessionStorage.setItem(`${type}_menus`, JSON.stringify(globalMenus))
        }
        return !value
      })
    },
  })
  const maxHeight = React.useMemo(() => {
    if (!menuChildren) return 100
    const primaryChildrenLength = isPrimary ? menuChildren.length : 0
    let subChildrenLength = 0
    menuChildren.forEach(i => {
      if (!isPrimary) subChildrenLength += 1
      if (i.menuChildren) {
        subChildrenLength += i.menuChildren.length
        i.menuChildren.forEach(j => {
          if (j.menuChildren) {
            subChildrenLength += j.menuChildren.length
          }
        })
      }
    })
    return primaryChildrenLength * 1.5 * 28 + subChildrenLength * 1.5 * 26 + 32
  }, [menuChildren])
  return (
    <MenuItemContainer hasArrow={hasArrow} isExpand={expand} maxHeight={maxHeight}>
      {newTitle}
      {children}
    </MenuItemContainer>
  )
}

export const Menus = ({ menus, type = 'product' }) => {
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const getActiveStatus = React.useCallback(
    (slug, type) => {
      return path.replace(/\/$/, '') === `/support/document/${type}/${slug}`
    },
    [path],
  )
  const handleClick = menu => {
    if (!menu.menuChildren && menu.slug) {
      Navigation.to({ link: `/support/document/${menu.type}/${menu.slug}` })
    }
  }

  return (
    <MenuWrap>
      {menus.map((menu, menuIndex) => {
        const activeIndex =
          menu.menuChildren && menu.menuChildren.findIndex(item => getActiveStatus(item.slug, item.type))
        return (
          <ItemContainer
            key={`primary-${menuIndex}`}
            type={type}
            slug={menu.slug}
            isPrimary
            menuChildren={menu.menuChildren}
            title={menu.menuChildren ? <PrimaryMenuItem hasArrow={true}> {menu.title}</PrimaryMenuItem> : <></>}
          >
            {!menu.menuChildren && (
              <PrimaryMenuItem
                actived={!menu.menuChildren && getActiveStatus(menu.slug, menu.type)}
                hasCursor={true}
                hasArrow={!!menu.menuChildren}
                onClick={() => {
                  handleClick(menu, menuIndex)
                }}
              >
                {menu.title}
              </PrimaryMenuItem>
            )}

            <SubMenuWrap>
              {menu.menuChildren &&
                menu.menuChildren.map((secondaryMenu, secondaryMenuIndex) => {
                  return (
                    <React.Fragment key={`secondaryMenu-${secondaryMenuIndex}`}>
                      <ItemContainer
                        key={`primary-${menuIndex}`}
                        type={type}
                        slug={secondaryMenu.slug}
                        menuChildren={secondaryMenu.menuChildren}
                        title={
                          secondaryMenu.menuChildren ? (
                            <SecondaryMenuItem hasArrow={true}> {secondaryMenu.title}</SecondaryMenuItem>
                          ) : (
                            <></>
                          )
                        }
                      >
                        {!secondaryMenu.menuChildren && (
                          <SecondaryMenuItem
                            actived={getActiveStatus(secondaryMenu.slug, secondaryMenu.type)}
                            hasCursor={!secondaryMenu.menuChildren}
                            onClick={() => {
                              handleClick(secondaryMenu)
                            }}
                          >
                            {secondaryMenu.title}
                          </SecondaryMenuItem>
                        )}

                        {secondaryMenu.menuChildren &&
                          secondaryMenu.menuChildren.map((tertiaryMenu, tertiaryMenuIndex) => {
                            return (
                              <React.Fragment>
                                <ItemContainer
                                  key={`primary-${tertiaryMenuIndex}`}
                                  type={type}
                                  slug={tertiaryMenu.slug}
                                  menuChildren={tertiaryMenu.menuChildren}
                                  title={
                                    tertiaryMenu.menuChildren ? (
                                      <TertiaryMenuItem hasArrow={true}> {tertiaryMenu.title}</TertiaryMenuItem>
                                    ) : (
                                      <></>
                                    )
                                  }
                                >
                                  {!tertiaryMenu.menuChildren && (
                                    <TertiaryMenuItem
                                      key={`tertiaryMenu-${tertiaryMenuIndex}`}
                                      hasCursor={!tertiaryMenu.menuChildren}
                                      actived={
                                        !tertiaryMenu.menuChildren &&
                                        getActiveStatus(tertiaryMenu.slug, tertiaryMenu.type)
                                      }
                                      onClick={() => {
                                        handleClick(tertiaryMenu)
                                      }}
                                    >
                                      {tertiaryMenu.title}
                                    </TertiaryMenuItem>
                                  )}
                                  {tertiaryMenu.menuChildren &&
                                    tertiaryMenu.menuChildren.map((fourthMenu, fourthMenuIndex) => {
                                      return (
                                        <React.Fragment>
                                          <FourthMenuItem
                                            key={`fourthMenu-${fourthMenuIndex}`}
                                            hasCursor={!fourthMenu.menuChildren}
                                            actived={
                                              !fourthMenu.menuChildren &&
                                              getActiveStatus(fourthMenu.slug, fourthMenu.type)
                                            }
                                            onClick={() => {
                                              handleClick(fourthMenu)
                                            }}
                                          >
                                            {fourthMenu.title}
                                          </FourthMenuItem>
                                        </React.Fragment>
                                      )
                                    })}
                                </ItemContainer>
                              </React.Fragment>
                            )
                          })}
                      </ItemContainer>
                    </React.Fragment>
                  )
                })}
            </SubMenuWrap>
          </ItemContainer>
        )
      })}
    </MenuWrap>
  )
}
